// AIDEV-NOTE: Modal de compartilhamento de pedido via WhatsApp, Email, Download ou Visualizacao
// Cada botao tem loading state independente. Erros exibidos via Snackbar/Alert MUI.

import { useState, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  CircularProgress,
  Snackbar,
  Alert,
  Typography
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import EmailIcon from '@mui/icons-material/Email';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import type { Pedido } from '@azoupvendas/shared';
import { pdfService, type EmpresaData, type VendedorData } from '../services/pdf.service';

interface ShareModalProps {
  open: boolean;
  onClose: () => void;
  pedido: Pedido;
  empresa?: Partial<EmpresaData>;
  vendedor?: VendedorData;
}

type ShareAction = 'whatsapp' | 'email' | 'download' | 'preview';

export function ShareModal({ open, onClose, pedido, empresa, vendedor }: ShareModalProps) {
  const [loadingAction, setLoadingAction] = useState<ShareAction | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleAction = useCallback(
    async (action: ShareAction) => {
      setLoadingAction(action);
      setError(null);

      try {
        switch (action) {
          case 'whatsapp':
            await pdfService.compartilharWhatsApp(pedido, empresa, vendedor);
            break;
          case 'email':
            await pdfService.compartilharEmail(pedido, empresa, vendedor);
            break;
          case 'download':
            await pdfService.download(pedido, empresa, vendedor);
            break;
          case 'preview':
            await pdfService.previsualizar(pedido, empresa, vendedor);
            break;
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Erro ao processar compartilhamento';
        setError(message);
      } finally {
        setLoadingAction(null);
      }
    },
    [pedido, empresa, vendedor]
  );

  const isLoading = (action: ShareAction) => loadingAction === action;
  const isDisabled = loadingAction !== null;

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle>Compartilhar Pedido</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Pedido {pedido.numero ? `N. ${pedido.numero}` : '(Rascunho)'} - {pedido.cliente_nome}
          </Typography>

          <Stack spacing={1.5}>
            {/* WhatsApp */}
            <Button
              variant="contained"
              fullWidth
              startIcon={
                isLoading('whatsapp') ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <WhatsAppIcon />
                )
              }
              disabled={isDisabled}
              onClick={() => handleAction('whatsapp')}
              sx={{
                backgroundColor: '#25D366',
                '&:hover': { backgroundColor: '#1DA851' },
                textTransform: 'none'
              }}
            >
              WhatsApp
            </Button>

            {/* Email */}
            <Button
              variant="contained"
              fullWidth
              startIcon={
                isLoading('email') ? <CircularProgress size={20} color="inherit" /> : <EmailIcon />
              }
              disabled={isDisabled}
              onClick={() => handleAction('email')}
              sx={{ textTransform: 'none' }}
            >
              Email
            </Button>

            {/* Download */}
            <Button
              variant="contained"
              fullWidth
              color="inherit"
              startIcon={
                isLoading('download') ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <DownloadIcon />
                )
              }
              disabled={isDisabled}
              onClick={() => handleAction('download')}
              sx={{ textTransform: 'none' }}
            >
              Download PDF
            </Button>

            {/* Visualizar */}
            <Button
              variant="outlined"
              fullWidth
              color="inherit"
              startIcon={
                isLoading('preview') ? (
                  <CircularProgress size={20} color="inherit" />
                ) : (
                  <VisibilityIcon />
                )
              }
              disabled={isDisabled}
              onClick={() => handleAction('preview')}
              sx={{ textTransform: 'none' }}
            >
              Visualizar PDF
            </Button>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} disabled={isDisabled}>
            Fechar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar de erro */}
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setError(null)} variant="filled">
          {error}
        </Alert>
      </Snackbar>
    </>
  );
}
